import { formatIsoDateShort } from "@/lib/bookingDates";
import type {
  WebsiteNewsDisplayStatus,
  WebsiteNewsItem,
  WebsiteNewsLocale,
  WebsiteNewsWebsitePreview,
} from "@/types/websiteNews";
import { monthDayInRecurring } from "@shared/monthDayInRecurring";

function isInSchedule(item: WebsiteNewsItem, referenceDate: Date): boolean {
  if (item.schedule_mode === "always") return true;
  if (item.schedule_mode === "fixed") {
    if (!item.valid_from || !item.valid_until) return false;
    const ref = referenceDate.toISOString().slice(0, 10);
    return item.valid_from <= ref && ref <= item.valid_until;
  }
  if (!item.start_month || !item.start_day || !item.end_month || !item.end_day) return false;
  return monthDayInRecurring(
    referenceDate.getMonth() + 1,
    referenceDate.getDate(),
    item.start_month,
    item.start_day,
    item.end_month,
    item.end_day,
  );
}

function isActiveOn(item: WebsiteNewsItem, referenceDate: Date): boolean {
  return item.is_active && isInSchedule(item, referenceDate);
}

function validItems(items: WebsiteNewsItem[]): WebsiteNewsItem[] {
  return items.filter((item) => item.title.trim() && item.body.trim());
}

function selectActiveNews(
  items: WebsiteNewsItem[],
  locale: WebsiteNewsLocale,
  referenceDate: Date,
): WebsiteNewsItem | null {
  const active = validItems(items)
    .filter((item) => item.locale === locale && isActiveOn(item, referenceDate))
    .sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title));
  return active[0] ?? null;
}

export function classifyWebsiteNewsDisplayStatus(
  item: WebsiteNewsItem,
  items: WebsiteNewsItem[],
  referenceDate: Date,
): WebsiteNewsDisplayStatus {
  if (!item.title.trim() || !item.body.trim()) return "disabled";
  if (!item.is_active) return "disabled";
  if (!isInSchedule(item, referenceDate)) return "outside_period";
  const selected = selectActiveNews(items, item.locale, referenceDate);
  return selected?.id === item.id ? "live" : "superseded";
}

export function supersededByTitle(
  item: WebsiteNewsItem,
  items: WebsiteNewsItem[],
  referenceDate: Date,
): string | null {
  const selected = selectActiveNews(items, item.locale, referenceDate);
  if (!selected || selected.id === item.id) return null;
  return selected.title;
}

export function buildWebsiteNewsPreview(
  items: WebsiteNewsItem[],
  referenceDate = new Date(),
): WebsiteNewsWebsitePreview {
  const ref = referenceDate.toISOString().slice(0, 10);
  const active = (["de", "en"] as const).map((locale) => {
    const selected = selectActiveNews(items, locale, referenceDate);
    return {
      locale,
      item_id: selected?.id ?? null,
      title: selected?.title ?? null,
    };
  });
  return { reference_date: ref, active };
}

export function websiteDisplayStatusLabel(status: WebsiteNewsDisplayStatus): string {
  switch (status) {
    case "live":
      return "Aktuell auf der Website";
    case "disabled":
      return "Deaktiviert";
    case "outside_period":
      return "Außerhalb des Anzeigezeitraums";
    case "superseded":
      return "Wird nicht angezeigt (niedrigere Sortierung)";
  }
}

export function websiteDisplayStatusClass(status: WebsiteNewsDisplayStatus): string {
  switch (status) {
    case "live":
      return "border-status-green/40 bg-status-green/10 text-status-green";
    case "disabled":
      return "border-separator bg-surface-secondary text-label-secondary";
    case "outside_period":
      return "border-status-amber/40 bg-status-amber/10 text-status-amber";
    case "superseded":
      return "border-status-amber/40 bg-status-amber/10 text-status-amber";
  }
}

export function formatWebsitePreviewDate(referenceDate: string): string {
  return formatIsoDateShort(referenceDate);
}
